/**
 * Bid Flow Component
 * Handles the bid submission flow with state management
 */

class BidFlow {
  constructor(element) {
    this.element = element;
    this.productSku = element.dataset.productSku;
    this.variantId = element.dataset.variantId;
    this.initialPrice = element.dataset.initialPrice;
    this.originalPrice = element.dataset.originalPrice;
    // Currency for the active market (set by Liquid from cart.currency.iso_code).
    // Falls back to SEK if the data attribute is missing (legacy templates).
    this.currency = element.dataset.currency || 'SEK';
    this.currentState = 'initial';

    // Cache DOM elements
    this.priceInput = element.querySelector('[data-price-input]');
    this.checkBtn = element.querySelector('[data-check-bid]');
    this.saveBtn = element.querySelector('[data-save-bid]');
    this.emailInput = element.querySelector('[data-email-input]');
    this.emailSection = element.querySelector('[data-email-section]');
    this.checkoutSection = element.querySelector('[data-checkout-section]');
    this.addToCartBtn = element.querySelector('[data-add-to-cart]');
    this.states = {
      initial: element.querySelector('[data-state="initial"]'),
      loading: element.querySelector('[data-state="loading"]'),
      success: element.querySelector('[data-state="success"]'),
      confirmation: element.querySelector('[data-state="confirmation"]')
    };
    this.successTitle = element.querySelector('.bid-flow__success-title');
    this.successText = element.querySelector('.bid-flow__success-text');
    this.confirmationTitle = element.querySelector('.bid-flow__confirmation-title');
    this.confirmationText = element.querySelector('.bid-flow__confirmation-text');

    this.init();
  }

  init() {
    // Bind event listeners
    if (this.checkBtn) {
      this.checkBtn.addEventListener('click', () => this.handleSubmit());
    }

    if (this.saveBtn) {
      this.saveBtn.addEventListener('click', () => this.savePriceProposal());
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.handleCheckout());
    }

    // Allow Enter key to submit
    if (this.priceInput) {
      this.priceInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSubmit();
        }
      });

      // Clear validation message when user types in price input
      this.priceInput.addEventListener('input', (e) => {
        e.currentTarget.setCustomValidity('');
      });
    }

    // Clear validation message when user types in email input
    if (this.emailInput) {
      this.emailInput.addEventListener('input', (e) => {
        e.currentTarget.setCustomValidity('');
      });
    }
  }

  /**
   * Change the current state
   */
  setState(newState) {
    if (!this.states[newState]) {
      console.error(`Invalid state: ${newState}`);
      return;
    }

    // Hide all states
    Object.values(this.states).forEach(state => {
      if (state) state.style.display = 'none';
    });

    // Show the new state
    this.states[newState].style.display = 'block';
    this.currentState = newState;
  }

  /**
   * Handle bid submission
   */
  async handleSubmit() {
    const bidAmount = this.priceInput.value;

    // Validate input
    if (!bidAmount || bidAmount <= 0) {
      alert('Vänligen ange ett giltigt pris');
      return;
    }

    // Disable check button
    if (this.checkBtn) {
      this.checkBtn.disabled = true;
    }

    // Show loading state
    this.setState('loading');

    try {
      // API call
      const result = await this.checkBid(bidAmount);

      // Check if we got a valid bid response
      if (result && result.bidResponse) {
        if (result.bidResponse === 'too_low') {
          // Calculate 50% of original price: change to show the amount we get from backend
          const minAmount = result.minAmount || Math.round(this.originalPrice / 100 / 2);

          // Return to initial state first
          this.setState('initial');

          // Re-enable check button
          if (this.checkBtn) {
            this.checkBtn.disabled = false;
          }

          // Set custom validation message on price input after state change
          if (this.priceInput) {
            setTimeout(() => {
              this.priceInput.setCustomValidity(`Lägg minst ${minAmount} kr`);
              this.priceInput.reportValidity();
            }, 100);
          }

          return; // Don't proceed to success state
        }

        // Set dynamic content based on API response
        this.setSuccessContent(result, bidAmount);

        // Add delay before showing success state
        await new Promise(resolve => setTimeout(resolve, 500));

        // Show success state
        this.setState('success');
      } else {
        throw new Error(result.message || 'Failed to check bid');
      }
    } catch (error) {
      console.error('Bid check error:', error);

      // Return to initial state
      this.setState('initial');

      // Re-enable check button
      if (this.checkBtn) {
        this.checkBtn.disabled = false;
      }
    }
  }

  /**
   * API call to submit bid
   * Calls /checkPriceProposal with proposed price and item id
   */
  async checkBid(amount) {
    try {
      const response = await fetch(`${window.API_BASE_URL}/checkPriceProposal?itemId=${this.productSku}&price=${amount}&variantId=${this.variantId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Log to console for debugging
      console.log('Bid checked:', {
        productSku: this.productSku,
        variantId: this.variantId,
        bidAmount: amount,
        currency: this.currency,
        response: data
      });

      return data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }

  /**
   * Format price for display
   */
  formatPrice(amount) {
    // Remove decimals and add proper spacing
    const formatted = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return formatted;
  }

  /**
   * Set success content based on API response
   */
  setSuccessContent(result, bidAmount) {
    const { bidResponse, daysUntilAccept, discountUrl, priceStrategy } = result;
    const price = this.formatPrice(bidAmount);

    let title = '';
    let text = '';
    let isDirectAccept = false;

    if (bidResponse === 'accept_direct') {
      title = 'Vi accepterar ditt pris';
      text = `Nu kan du köpa den för <span class="price-in-text">${price} kr</span> en dag innan alla andra. Imorgon sänks priset på varan.`;
      isDirectAccept = true;
    } else if (bidResponse === 'accept_tomorrow') {
      title = 'Förtur på priset';
      text = `Om den inte säljs under dagen så kommer du att erbjudas köpa den för <span class="price-in-text">${price} kr</span> redan imorgon, innan priset sänks för allmänheten. Vi mailar dig när det är dags.`;
    } else if (bidResponse === 'accept_before_min' || bidResponse === 'accept_before_mid') {
      if (daysUntilAccept <= 6){
        title = 'Förtur på priset';
        text = `Om den inte säljs närmaste dagarna så kommer du erbjudas att köpa den för <span class="price-in-text">${price} kr</span> innan priset sänks för allmänheten. Vi mailar dig när det är dags.`;
      }
      else if(daysUntilAccept <= 10){
        title = 'Vi hör av oss';
        text = `<span class="price-in-text">${price} kr</span> är lite lågt just nu, men vi sparar ditt prisförslag och hör av oss om plagget inte skulle säljas inom en vecka.`;
      }
      else {
        title = 'Vi hör av oss';
        text = `<span class="price-in-text">${price} kr</span> är lite lågt just nu, men vi sparar ditt prisförslag och kan höra av oss om plagget inte skulle säljas närmsta veckan.`;
      }
    } else if (bidResponse === 'below_min_check_back') {
        title = 'Vi avvaktar';
        text = `Tack för ditt förslag på <span class="price-in-text">${price} kr</span>, men vi avvaktar lite. Om vi kommer till en punkt där vi tänka oss ditt pris, så mailar vi dig gärna för att höra om du fortfarande är intresserad.`;
    } else if (bidResponse === 'below_min_ask_seller') {
      title = 'Vi frågar säljaren';
      text = `Vi behöver fråga säljaren om priset du ber om är okej. Vi återkommer om du får köpa den för <span class="price-in-text">${price} kr.</span>`;
    } else {
      // Default fallback
      title = bidResponse;
      text = `Something went wrong, please contact us if problem persists.`;
    }

    // Update the title and text elements
    if (this.successTitle) {
      this.successTitle.textContent = title;
    }
    if (this.successText) {
      this.successText.innerHTML = text;
    }

    // Store data for button click
    this.currentBidAmount = bidAmount;
    this.currentBidResponse = bidResponse;
    this.discountUrl = discountUrl;
    this.daysUntilAccept = daysUntilAccept;
    this.priceStrategy = priceStrategy;

    // Toggle between email section and checkout section
    if (isDirectAccept) {
      // Show checkout button, hide email section
      if (this.emailSection) {
        this.emailSection.style.display = 'none';
      }
      if (this.checkoutSection) {
        this.checkoutSection.style.display = 'block';
      }
      // Update button text with price
      if (this.addToCartBtn) {
        this.addToCartBtn.textContent = `Köp nu för ${price} kr`;
      }
    } else {
      // Show email section, hide checkout button
      if (this.emailSection) {
        this.emailSection.style.display = 'block';
      }
      if (this.checkoutSection) {
        this.checkoutSection.style.display = 'none';
      }
      // Prefill email field if not already filled
      this.prefillEmail();
    }
  }

  /**
   * Prefill email field with stored email if not already filled
   */
  prefillEmail() {
    if (!this.emailInput) return;

    // Only prefill if the email field is empty
    if (!this.emailInput.value || this.emailInput.value.trim() === '') {
      const storedEmail = sessionStorage.getItem('bidFlowEmail');
      // Bids saved before the snippet stopped prefilling the placeholder stored it here too
      if (storedEmail && !this.isPlaceholderEmail(storedEmail)) {
        this.emailInput.value = storedEmail;
      }
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * The address Functions gives a phone-only app user's Shopify customer (user_<uid>@maiapp.se).
   * No inbox receives it, so a bid saved to it is never answered.
   */
  isPlaceholderEmail(email) {
    return /^user_[^@]*@maiapp\.se$/i.test(email.trim());
  }

  /**
   * Handle direct checkout button click
   */
  handleCheckout() {
    if (this.discountUrl) {
      // Redirect to the discount URL
      window.location.href = this.discountUrl;
    } else {
      console.error('No discount URL available');
      alert('Något gick fel. Försök igen.');
    }
  }

  /**
   * Save price proposal
   */
  async savePriceProposal() {
    const email = this.emailInput ? this.emailInput.value.trim() : '';

    // Validate email
    if (!email) {
      this.emailInput.setCustomValidity('Fyll i din e-postadress');
      this.emailInput.reportValidity();
      return;
    }
    if (!this.isValidEmail(email)) {
      this.emailInput.setCustomValidity('Ange en giltig e-postadress');
      this.emailInput.reportValidity();
      return;
    }
    if (this.isPlaceholderEmail(email)) {
      this.emailInput.setCustomValidity('Fyll i din egen e-postadress');
      this.emailInput.reportValidity();
      return;
    }

    // Disable button and show loading state
    this.setSaveButtonLoading(true);

    // Make POST request to submit the price proposal
    if (this.currentBidAmount && this.currentBidResponse) {
      try {
        await this.submitPriceProposal({
          price: this.currentBidAmount,
          email: email,
          itemId: this.productSku,
          variantId: this.variantId,
          acceptedBidResponse: this.currentBidResponse,
          daysUntilAccept: this.daysUntilAccept,
          priceStrategy: this.priceStrategy
        });

        // Store email in sessionStorage for future use
        sessionStorage.setItem('bidFlowEmail', email);

        // Show confirmation state
        this.setState('confirmation');

        // Close the bottomsheet after 2 seconds
        setTimeout(() => {
          this.handleClose();
        }, 1200);

      } catch (error) {
        console.error('Failed to submit price proposal:', error);
        alert('Något gick fel. Försök igen.');
        // Re-enable button on error
        this.setSaveButtonLoading(false);
      }
    }
  }

  /**
   * Submit price proposal to API
   */
  async submitPriceProposal(data) {
    try {
      const response = await fetch(`${window.API_BASE_URL}/priceProposal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Log to console for debugging
      console.log('Price proposal submitted:', {
        data: data,
        response: result
      });

      return result;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }

  /**
   * Set save button loading state
   */
  setSaveButtonLoading(isLoading) {
    const saveBtn = this.element.querySelector('[data-save-bid]');
    const saveText = this.element.querySelector('.bid-flow__save-text');
    const saveLoading = this.element.querySelector('.bid-flow__save-loading');

    if (saveBtn && saveText && saveLoading) {
      if (isLoading) {
        // Disable button and show loading
        saveBtn.disabled = true;
        saveBtn.style.cursor = 'not-allowed';
        saveText.style.display = 'none';
        saveLoading.style.display = 'block';
      } else {
        // Re-enable button and hide loading
        saveBtn.disabled = false;
        saveBtn.style.cursor = 'pointer';
        saveText.style.display = 'inline';
        saveLoading.style.display = 'none';
      }
    }
  }

  /**
   * Handle close button click
   */
  handleClose() {
    // Find and close the parent bottomsheet
    const bottomsheet = this.element.closest('.bottomsheet-overlay');
    if (bottomsheet) {
      // Trigger bottomsheet close
      const closeBtn = bottomsheet.querySelector('.bottomsheet__close');
      if (closeBtn) {
        closeBtn.click();
      }
    }

    // Reset to initial state after a short delay
    setTimeout(() => {
      this.resetToInitial();
    }, 300);
  }

  /**
   * Reset bid flow to initial state
   */
  resetToInitial() {
    this.setState('initial');
    if (this.priceInput) {
      // initialPrice is in cents, so divide by 100 to get SEK
      this.priceInput.value = Math.round(this.initialPrice / 100);
    }
    if (this.checkBtn) {
      this.checkBtn.disabled = false;
    }
    // Reset section visibility to default (email section visible, checkout hidden)
    if (this.emailSection) {
      this.emailSection.style.display = 'block';
    }
    if (this.checkoutSection) {
      this.checkoutSection.style.display = 'none';
    }
    // Reset save button loading state
    this.setSaveButtonLoading(false);
    // Clear stored bid data
    this.currentBidAmount = null;
    this.currentBidResponse = null;
    this.discountUrl = null;
  }
}

// Initialize all bid flows on the page
document.addEventListener('DOMContentLoaded', () => {
  const bidFlows = document.querySelectorAll('[data-bid-flow]');
  bidFlows.forEach(element => {
    if (!element.bidFlowInitialized) {
      element.bidFlowInstance = new BidFlow(element);
      element.bidFlowInitialized = true;
    }
  });
});

// Also initialize when bottomsheet is dynamically loaded
if (typeof window.addEventListener !== 'undefined') {
  window.addEventListener('bottomsheet:opened', () => {
    const bidFlows = document.querySelectorAll('[data-bid-flow]');
    bidFlows.forEach(element => {
      if (!element.bidFlowInitialized) {
        element.bidFlowInstance = new BidFlow(element);
        element.bidFlowInitialized = true;
      }
    });
  });

  // Helper function to reset bid flow when bottomsheet closes
  const resetBidFlowOnClose = (overlay) => {
    if (!overlay) return;

    const bidFlow = overlay.querySelector('[data-bid-flow]');
    if (!bidFlow || !bidFlow.bidFlowInstance) return;

    if (bidFlow.bidFlowInstance.currentState !== 'initial') {
      bidFlow.bidFlowInstance.resetToInitial();
    }
  };

  // Reset bid flow when bottomsheet close button is clicked
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-bottomsheet-close]')) {
      resetBidFlowOnClose(e.target.closest('[data-bottomsheet-overlay]'));
    }
  });

  // Reset bid flow when clicking outside the bottomsheet
  document.addEventListener('click', (e) => {
    const overlay = e.target.closest('[data-bottomsheet-overlay]');
    if (overlay && e.target === overlay) {
      resetBidFlowOnClose(overlay);
    }
  });

  // Reset bid flow on ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      resetBidFlowOnClose(document.querySelector('.bottomsheet-overlay--active'));
    }
  });
}

